import React, { useState, useMemo, useCallback, useEffect } from 'react';
import { Table, Button, Modal, Form, Badge, Spinner, Alert, InputGroup } from 'react-bootstrap';
import { PlusCircle, PencilSquare, Trash, XCircle, Search } from 'react-bootstrap-icons';
import RESectionHeader from './RESectionHeader';
import { useGetUserPreferencesQuery, useUpdateUserPreferencesMutation } from '../../services/api';
import {
  preferencesTenantWriteSpread,
  useTenantIdForPreferences,
  useUserPreferencesQueryArg,
} from '../../hooks/useTenantIdForPreferences';

interface Expense {
  id: string;
  name: string;
  amount: number;
  frequency: 'monthly' | 'yearly' | 'one-time';
  categories: string[];
  notes: string;
  createdAt: string;
  updatedAt: string;
}

const DEFAULT_CATEGORIES = [
  'Contractors',
  'Developer',
  'Employees',
  'Hosting',
  'Marketing',
  'Operations',
  'Promotions',
  'Software',
];

const FREQUENCY_LABELS: Record<string, string> = {
  monthly: 'Monthly',
  yearly: 'Yearly',
  'one-time': 'One-time',
};

const CATEGORY_COLORS: Record<string, string> = {
  Hosting: 'primary',
  Software: 'info',
  Marketing: 'success',
  Promotions: 'warning',
  Employees: 'secondary',
  Contractors: 'dark',
  Developer: 'danger',
  Operations: 'primary',
};

function genId(): string {
  return `exp-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
}

/** RTK/Immer may freeze preference arrays; never put those refs in local state (e.g. .sort() mutates in place). */
function cloneExpensesFromApi(raw: unknown): Expense[] {
  if (!Array.isArray(raw)) return [];
  return raw.map((e: any, i: number) => ({
    id: String(e?.id ?? '').trim() || `exp-fallback-${i}`,
    name: String(e?.name ?? ''),
    amount: Number(e?.amount) || 0,
    frequency: (['monthly', 'yearly', 'one-time'].includes(String(e?.frequency))
      ? e.frequency
      : 'monthly') as Expense['frequency'],
    categories: Array.isArray(e?.categories) ? [...e.categories] : [],
    notes: String(e?.notes ?? ''),
    createdAt: String(e?.createdAt ?? new Date().toISOString()),
    updatedAt: String(e?.updatedAt ?? new Date().toISOString()),
  }));
}

function formatCurrency(amount: number): string {
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount);
}

function monthlyEquivalent(expense: Expense): number {
  if (expense.frequency === 'yearly') return expense.amount / 12;
  if (expense.frequency === 'one-time') return 0;
  return expense.amount;
}

function yearlyEquivalent(expense: Expense): number {
  if (expense.frequency === 'monthly') return expense.amount * 12;
  if (expense.frequency === 'one-time') return expense.amount;
  return expense.amount;
}

export default function REExpenses() {
  const tenantId = useTenantIdForPreferences();
  const userPreferencesQueryArg = useUserPreferencesQueryArg();

  const { data: prefsData, isLoading } = useGetUserPreferencesQuery(userPreferencesQueryArg, {
    refetchOnMountOrArgChange: true,
  });
  const [updatePrefs, { isLoading: isSaving }] = useUpdateUserPreferencesMutation();

  const preferences: any = (prefsData as any)?.preferences || {};
  const savedExpenses: Expense[] = useMemo(() => {
    return cloneExpensesFromApi(preferences.expenses);
  }, [preferences.expenses]);

  const [expenses, setExpenses] = useState<Expense[]>([]);
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    if (savedExpenses.length > 0 || (!isLoading && expenses.length === 0 && !dirty)) {
      setExpenses(savedExpenses);
      setDirty(false);
    }
  }, [savedExpenses, isLoading]);

  const [showModal, setShowModal] = useState(false);
  const [editingExpense, setEditingExpense] = useState<Expense | null>(null);
  const [formName, setFormName] = useState('');
  const [formAmount, setFormAmount] = useState('');
  const [formFrequency, setFormFrequency] = useState<'monthly' | 'yearly' | 'one-time'>('monthly');
  const [formCategories, setFormCategories] = useState<string[]>([]);
  const [formNotes, setFormNotes] = useState('');
  const [customCategory, setCustomCategory] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [filterCategory, setFilterCategory] = useState('');
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null);

  const allCategories = useMemo(() => {
    const custom = expenses.flatMap((e) => e.categories);
    const merged = new Set([...DEFAULT_CATEGORIES, ...custom]);
    return Array.from(merged).sort();
  }, [expenses]);

  const filteredExpenses = useMemo(() => {
    let result = expenses;
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      result = result.filter(
        (e) =>
          e.name.toLowerCase().includes(term) ||
          e.notes.toLowerCase().includes(term) ||
          e.categories.some((c) => c.toLowerCase().includes(term))
      );
    }
    if (filterCategory) {
      result = result.filter((e) => e.categories.includes(filterCategory));
    }
    return result;
  }, [expenses, searchTerm, filterCategory]);

  const totals = useMemo(() => {
    const monthly = filteredExpenses.reduce((sum, e) => sum + monthlyEquivalent(e), 0);
    const yearly = filteredExpenses.reduce((sum, e) => sum + yearlyEquivalent(e), 0);
    const oneTime = filteredExpenses.filter((e) => e.frequency === 'one-time').reduce((sum, e) => sum + e.amount, 0);
    return { monthly, yearly, oneTime };
  }, [filteredExpenses]);

  const persist = useCallback(
    async (next: Expense[]) => {
      setExpenses(next);
      setDirty(true);
      try {
        await updatePrefs({
          ...preferencesTenantWriteSpread(tenantId),
          preferences: { expenses: next },
        }).unwrap();
        setDirty(false);
      } catch (err) {
        console.error('Failed to save expenses:', err);
      }
    },
    [updatePrefs, tenantId]
  );

  const openAddModal = () => {
    setEditingExpense(null);
    setFormName('');
    setFormAmount('');
    setFormFrequency('monthly');
    setFormCategories([]);
    setFormNotes('');
    setCustomCategory('');
    setShowModal(true);
  };

  const openEditModal = (expense: Expense) => {
    setEditingExpense(expense);
    setFormName(expense.name);
    setFormAmount(String(expense.amount));
    setFormFrequency(expense.frequency);
    setFormCategories([...expense.categories]);
    setFormNotes(expense.notes);
    setCustomCategory('');
    setShowModal(true);
  };

  const handleSave = () => {
    const amount = parseFloat(formAmount);
    if (!formName.trim() || isNaN(amount) || amount < 0) return;

    const now = new Date().toISOString();
    if (editingExpense) {
      const updated = expenses.map((e) =>
        e.id === editingExpense.id
          ? { ...e, name: formName.trim(), amount, frequency: formFrequency, categories: formCategories, notes: formNotes.trim(), updatedAt: now }
          : e
      );
      persist(updated);
    } else {
      const newExpense: Expense = {
        id: genId(),
        name: formName.trim(),
        amount,
        frequency: formFrequency,
        categories: formCategories,
        notes: formNotes.trim(),
        createdAt: now,
        updatedAt: now,
      };
      persist([...expenses, newExpense]);
    }
    setShowModal(false);
  };

  const handleDelete = (id: string) => {
    persist(expenses.filter((e) => e.id !== id));
    setDeleteConfirm(null);
  };

  const toggleCategory = (cat: string) => {
    setFormCategories((prev) => (prev.includes(cat) ? prev.filter((c) => c !== cat) : [...prev, cat]));
  };

  const addCustomCategory = () => {
    const trimmed = customCategory.trim();
    if (trimmed && !formCategories.includes(trimmed)) {
      setFormCategories((prev) => [...prev, trimmed]);
    }
    setCustomCategory('');
  };

  if (isLoading) {
    return (
      <>
        <RESectionHeader title="Expenses" showHelp />
        <div className="d-flex justify-content-center align-items-center p-5">
          <Spinner animation="border" />
        </div>
      </>
    );
  }

  return (
    <>
      <RESectionHeader title="Expenses" showHelp searchValue={searchTerm} onSearchChange={setSearchTerm} searchPlaceholder="Search expenses" />

      <div className="p-3">
        {/* Toolbar */}
        <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
          <div className="d-flex align-items-center gap-2 flex-wrap">
            <Form.Select
              size="sm"
              style={{ width: 'auto', fontSize: '0.8rem' }}
              value={filterCategory}
              onChange={(e) => setFilterCategory(e.target.value)}
            >
              <option value="">All Categories</option>
              {allCategories.map((cat) => (
                <option key={cat} value={cat}>{cat}</option>
              ))}
            </Form.Select>
            {filterCategory && (
              <Button variant="outline-secondary" size="sm" onClick={() => setFilterCategory('')} style={{ fontSize: '0.75rem' }}>
                <XCircle size={12} className="me-1" />Clear
              </Button>
            )}
            <span className="text-muted small">{filteredExpenses.length} expense{filteredExpenses.length !== 1 ? 's' : ''}</span>
          </div>
          <Button variant="primary" size="sm" onClick={openAddModal} disabled={isSaving}>
            <PlusCircle size={14} className="me-1" /> Add Expense
          </Button>
        </div>

        {/* Summary Cards */}
        <div className="d-flex gap-3 mb-3 flex-wrap">
          <div className="border rounded p-3 flex-fill" style={{ minWidth: 160, background: '#f8f9fa' }}>
            <div className="text-muted small">Monthly Recurring</div>
            <div className="fw-bold fs-5">{formatCurrency(totals.monthly)}</div>
          </div>
          <div className="border rounded p-3 flex-fill" style={{ minWidth: 160, background: '#f8f9fa' }}>
            <div className="text-muted small">Yearly Total</div>
            <div className="fw-bold fs-5">{formatCurrency(totals.yearly)}</div>
          </div>
          {totals.oneTime > 0 && (
            <div className="border rounded p-3 flex-fill" style={{ minWidth: 160, background: '#fff3cd' }}>
              <div className="text-muted small">One-time Costs</div>
              <div className="fw-bold fs-5">{formatCurrency(totals.oneTime)}</div>
            </div>
          )}
        </div>

        {/* Expenses Table */}
        {filteredExpenses.length === 0 ? (
          <Alert variant="light" className="text-center py-4">
            {expenses.length === 0
              ? 'No expenses yet. Click "Add Expense" to get started.'
              : 'No expenses match your search or filter.'}
          </Alert>
        ) : (
          <Table hover responsive size="sm" className="align-middle">
            <thead>
              <tr style={{ fontSize: '0.8rem' }}>
                <th>Name</th>
                <th>Categories</th>
                <th className="text-end">Amount</th>
                <th>Frequency</th>
                <th className="text-end">Monthly Equiv.</th>
                <th>Notes</th>
                <th style={{ width: 90 }}></th>
              </tr>
            </thead>
            <tbody>
              {[...filteredExpenses]
                .sort((a, b) => a.name.localeCompare(b.name))
                .map((expense) => (
                  <tr key={expense.id} style={{ fontSize: '0.82rem' }}>
                    <td className="fw-semibold">{expense.name}</td>
                    <td>
                      <div className="d-flex flex-wrap gap-1">
                        {expense.categories.map((cat) => (
                          <Badge
                            key={cat}
                            bg={CATEGORY_COLORS[cat] || 'secondary'}
                            style={{ fontSize: '0.65rem', fontWeight: 500, cursor: 'pointer' }}
                            onClick={() => setFilterCategory(cat)}
                          >
                            {cat}
                          </Badge>
                        ))}
                      </div>
                    </td>
                    <td className="text-end fw-semibold">{formatCurrency(expense.amount)}</td>
                    <td>{FREQUENCY_LABELS[expense.frequency]}</td>
                    <td className="text-end text-muted">
                      {expense.frequency === 'one-time' ? '—' : formatCurrency(monthlyEquivalent(expense))}
                    </td>
                    <td className="text-muted" style={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {expense.notes || '—'}
                    </td>
                    <td>
                      <div className="d-flex gap-1">
                        <Button variant="outline-secondary" size="sm" onClick={() => openEditModal(expense)} style={{ padding: '2px 6px' }}>
                          <PencilSquare size={12} />
                        </Button>
                        {deleteConfirm === expense.id ? (
                          <Button variant="danger" size="sm" onClick={() => handleDelete(expense.id)} style={{ padding: '2px 6px', fontSize: '0.7rem' }}>
                            Confirm
                          </Button>
                        ) : (
                          <Button variant="outline-danger" size="sm" onClick={() => setDeleteConfirm(expense.id)} style={{ padding: '2px 6px' }}>
                            <Trash size={12} />
                          </Button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
            </tbody>
            <tfoot>
              <tr style={{ fontSize: '0.82rem', fontWeight: 600, borderTop: '2px solid #dee2e6' }}>
                <td>Total ({filteredExpenses.length})</td>
                <td></td>
                <td className="text-end">{formatCurrency(filteredExpenses.reduce((s, e) => s + e.amount, 0))}</td>
                <td></td>
                <td className="text-end">{formatCurrency(totals.monthly)}</td>
                <td></td>
                <td></td>
              </tr>
            </tfoot>
          </Table>
        )}

        {isSaving && (
          <div className="text-muted small mt-2">
            <Spinner size="sm" animation="border" className="me-1" /> Saving...
          </div>
        )}
      </div>

      {/* Add / Edit Modal */}
      <Modal show={showModal} onHide={() => setShowModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title style={{ fontSize: '1.1rem' }}>{editingExpense ? 'Edit Expense' : 'Add Expense'}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group className="mb-3">
            <Form.Label className="small fw-semibold">Expense Name</Form.Label>
            <Form.Control
              type="text"
              value={formName}
              onChange={(e) => setFormName(e.target.value)}
              placeholder="e.g. AWS Hosting, SMTP.com Relay, John Doe"
              autoFocus
            />
          </Form.Group>

          <div className="d-flex gap-3 mb-3">
            <Form.Group className="flex-fill">
              <Form.Label className="small fw-semibold">Amount ($)</Form.Label>
              <InputGroup>
                <InputGroup.Text>$</InputGroup.Text>
                <Form.Control
                  type="number"
                  min="0"
                  step="0.01"
                  value={formAmount}
                  onChange={(e) => setFormAmount(e.target.value)}
                  placeholder="0.00"
                />
              </InputGroup>
            </Form.Group>
            <Form.Group style={{ minWidth: 140 }}>
              <Form.Label className="small fw-semibold">Frequency</Form.Label>
              <Form.Select value={formFrequency} onChange={(e) => setFormFrequency(e.target.value as any)}>
                <option value="monthly">Monthly</option>
                <option value="yearly">Yearly</option>
                <option value="one-time">One-time</option>
              </Form.Select>
            </Form.Group>
          </div>

          <Form.Group className="mb-3">
            <Form.Label className="small fw-semibold">Categories (select multiple)</Form.Label>
            <div className="d-flex flex-wrap gap-1 mb-2">
              {allCategories.map((cat) => (
                <Badge
                  key={cat}
                  bg={formCategories.includes(cat) ? (CATEGORY_COLORS[cat] || 'secondary') : 'light'}
                  text={formCategories.includes(cat) ? 'white' : 'dark'}
                  style={{ cursor: 'pointer', fontSize: '0.72rem', border: formCategories.includes(cat) ? 'none' : '1px solid #dee2e6' }}
                  onClick={() => toggleCategory(cat)}
                >
                  {formCategories.includes(cat) ? '✓ ' : ''}{cat}
                </Badge>
              ))}
            </div>
            <InputGroup size="sm">
              <Form.Control
                type="text"
                value={customCategory}
                onChange={(e) => setCustomCategory(e.target.value)}
                placeholder="Add custom category..."
                onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addCustomCategory(); } }}
              />
              <Button variant="outline-secondary" onClick={addCustomCategory} disabled={!customCategory.trim()}>
                Add
              </Button>
            </InputGroup>
          </Form.Group>

          <Form.Group className="mb-2">
            <Form.Label className="small fw-semibold">Notes (optional)</Form.Label>
            <Form.Control
              as="textarea"
              rows={2}
              value={formNotes}
              onChange={(e) => setFormNotes(e.target.value)}
              placeholder="Additional details about this expense..."
            />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" size="sm" onClick={() => setShowModal(false)}>Cancel</Button>
          <Button
            variant="primary"
            size="sm"
            onClick={handleSave}
            disabled={!formName.trim() || !formAmount || isNaN(parseFloat(formAmount)) || parseFloat(formAmount) < 0}
          >
            {editingExpense ? 'Save Changes' : 'Add Expense'}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
}
